// Stories made of clips on the Create feed (flag extend_clips; promptchan-backend docs/architecture/video.md,
// "Extend as separate clips"). In that flag an extension arrives as its own clip, not as its source and the new
// part merged, and its card remembers the video it continues (`redoOf.parentId`, with `asClip`). Followed back,
// those links make a chain: the video the story started from, then each clip in order. The feed shows a chain as one
// story card, where the newest of its clips would have been.
//
// A scene (flag story_new_scene) is the other kind of part: a video animated from a picture the person chose and
// added after the story's last clip (`sceneOf.parentId`). It follows that clip in the story but isn't made from it, so
// joining the story puts a cut there.
//
// Self-contained, so `node:test` can import it (tests/clip-chains.test.mjs).

export type ClipItem = {
  id: string
  type: string
  docRef?: string
  savedStatus?: string
  redoOf?: { parentId?: string; asClip?: boolean } | null
  sceneOf?: { parentId?: string } | null
}

export type FeedEntry<T> =
  | { kind: 'item'; item: T }
  // `at`: the card the story takes the place of, its newest in the feed (the feed's own scroll anchor reads its id)
  | { kind: 'chain'; id: string; at: T; clips: T[] }

/** Whether a feed item is a clip: an extension delivered as its own clip, continuing the video named. */
export const isClip = (item: ClipItem) =>
  item.type === 'video' && !!item.redoOf?.asClip && !!item.redoOf.parentId

/** Whether a feed item is a scene: a video added to a story after the one named, as a cut. */
export const isScene = (item: ClipItem) =>
  item.type === 'video' && !!item.sceneOf?.parentId

/** The video a clip or a scene comes after in its story, or undefined for anything else. */
export const storyParent = (item: ClipItem) =>
  isClip(item)
    ? item.redoOf!.parentId
    : isScene(item)
      ? item.sceneOf!.parentId
      : undefined

/**
 * The feed (newest first) with each story folded into one entry, at the place of its newest clip. A story is a
 * video and the clips that continue it, one after another; where a video has two clips continuing it (a Try again
 * whose first result was kept), the newest is the story's and the other stays a card of its own.
 */
export function foldClipChains<T extends ClipItem>(feed: T[]): FeedEntry<T>[] {
  const byRef = new Map<string, T>()
  for (const item of feed) if (item.docRef) byRef.set(item.docRef, item)

  // The newest clip or scene after each video: the feed is newest first, so the first one seen.
  const next = new Map<string, T>()
  for (const item of feed) {
    const parent = storyParent(item)
    if (parent && !next.has(parent)) next.set(parent, item)
  }

  const chainOf = new Map<string, string>() // item id -> its chain's id (the root's)
  const chains = new Map<string, T[]>()
  for (const item of feed) {
    if (!storyParent(item) || chainOf.has(item.id)) continue
    const root = rootOf(item, byRef)
    if (chainOf.has(root.id)) continue
    const clips = [root]
    for (
      let at = next.get(root.docRef ?? '');
      at;
      at = next.get(at.docRef ?? '')
    ) {
      if (clips.includes(at)) break // a link back to an earlier clip: never loop
      clips.push(at)
    }
    if (clips.length < 2) continue
    for (const clip of clips) chainOf.set(clip.id, root.id)
    chains.set(root.id, clips)
  }

  const entries: FeedEntry<T>[] = []
  const shown = new Set<string>()
  for (const item of feed) {
    const chainId = chainOf.get(item.id)
    if (!chainId) entries.push({ kind: 'item', item })
    else if (!shown.has(chainId)) {
      shown.add(chainId)
      entries.push({
        kind: 'chain',
        id: chainId,
        at: item,
        clips: chains.get(chainId)!,
      })
    }
  }
  return entries
}

/** The first video of the story a clip belongs to: its links followed back while the video they name is in the feed. */
function rootOf<T extends ClipItem>(item: T, byRef: Map<string, T>): T {
  const seen = new Set<string>()
  let at = item
  while (storyParent(at) && !seen.has(at.id)) {
    seen.add(at.id)
    const parent = byRef.get(storyParent(at)!)
    if (!parent) break
    at = parent
  }
  return at
}

/** The parts after `clip` in its story: the story goes on from it, so discarding or retrying it takes them too. */
export function clipsAfter<T extends ClipItem>(clips: T[], clip: T): T[] {
  const i = clips.indexOf(clip)
  return i < 0 ? [] : clips.slice(i + 1)
}
